import type {
  InterviewInsights,
  Section,
  WorkspacePrompt,
} from "@leaply-interview/shared-types";
import { auth } from "./firebase";

/**
 * Typed admin api client. Every request:
 *   1. Fetches a fresh Firebase ID token from `auth.currentUser`.
 *   2. Sends it as `Authorization: Bearer <token>`.
 *   3. Parses NestJS error responses into `ApiError` with the `error` code so
 *      callers can branch (e.g. 403 not_whitelisted vs 401 bad_token).
 *
 * No caching, no retries — wrap with TanStack Query at the page level when
 * we need that.
 */

const BASE_URL: string = import.meta.env.VITE_API_URL ?? "/api";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function getIdToken(): Promise<string> {
  const user = auth.currentUser;
  if (!user) throw new ApiError("Not signed in", 401, "unauthenticated");
  return user.getIdToken();
}

async function request<T>(
  path: string,
  init: RequestInit & { json?: unknown } = {},
): Promise<T> {
  const { json, headers, ...rest } = init;
  const finalHeaders = new Headers(headers);
  const token = await getIdToken();
  finalHeaders.set("Authorization", `Bearer ${token}`);
  if (json !== undefined) finalHeaders.set("Content-Type", "application/json");

  const res = await fetch(`${BASE_URL}${path}`, {
    ...rest,
    headers: finalHeaders,
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });

  if (!res.ok) {
    let code: string | null = null;
    let message = `${res.status} ${res.statusText}`;
    try {
      const body = (await res.json()) as { error?: unknown; message?: unknown };
      if (typeof body.error === "string") {
        code = body.error;
        message = body.error;
      } else if (typeof body.message === "string") {
        message = body.message;
      }
    } catch {
      /* not json */
    }
    // Hard nav to /not-authorized on the guard's 403. The user is signed
    // in (Firebase token is valid) but their email isn't on the
    // cms_users whitelist — every subsequent api call would 403 too, so
    // the in-place "Access denied" banner per-page is misleading chrome.
    //
    // window.location.replace beats useNavigate here: the api wrapper has
    // no router context, and we WANT the full reload so any half-rendered
    // page state is discarded. Skip if we're already on the dead-end so we
    // don't loop.
    if (
      res.status === 403 &&
      typeof window !== "undefined" &&
      window.location.pathname !== "/not-authorized"
    ) {
      window.location.replace("/not-authorized");
    }
    throw new ApiError(message, res.status, code);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ── Studies ─────────────────────────────────────────────────────────────────

// We re-export the StudyEntity shape loosely — the server returns the full
// row, but the admin only relies on a stable subset of fields.
export interface AdminStudy {
  id: string;
  slug: string;
  name: string;
  planTarget: string | null;
  systemPrompt: string;
  voice: string;
  status: string;
  studyType: string | null;
  description: string | null;
  interviewerName: string;
  firstMessage: string | null;
  /** Primary language ISO code (also `languages[0]`). Kept as a separate
   *  column so the participant flow can look it up without parsing the
   *  array. */
  language: string;
  /** Full list of supported languages — primary + any extras the agent
   *  should be willing to switch into. Null on legacy rows. */
  languages: string[] | null;
  /** Structured outline of main interview questions, grouped into named
   *  sections. Null = no structured outline (agent runs Discovery-mode off
   *  the system prompt alone). */
  sections: Section[] | null;
  maxValidInterviews: number | null;
  /** Call-length cap in minutes (2–60; default 60 = OpenAI hard limit). */
  maxDurationMinutes: number;
  rewardId: string | null;
  leaplyPlan: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdminStudySummaryRow {
  studyId: string;
  totalCount: number;
  validCount: number;
  invalidCount: number;
  inProgressCount: number;
  /** Archived interviews — hidden from the cards but still removed by a hard
   *  delete; the delete dialog adds these to totalCount for its true count. */
  archivedCount: number;
  totalUsd: number;
}

export const studies = {
  list: (): Promise<{ studies: AdminStudy[] }> => request("/studies"),
  summary: (): Promise<{ rows: AdminStudySummaryRow[] }> =>
    request("/studies/summary"),
  create: (): Promise<{ study: AdminStudy }> =>
    request("/studies", { method: "POST" }),
  get: (id: string): Promise<{ study: AdminStudy }> => request(`/studies/${id}`),
  update: (
    id: string,
    patch: Partial<AdminStudy>,
  ): Promise<{ study: AdminStudy }> =>
    request(`/studies/${id}`, { method: "PATCH", json: patch }),
  archive: (id: string): Promise<{ study: AdminStudy }> =>
    request(`/studies/${id}/archive`, { method: "POST" }),
  bulkArchive: (ids: string[]): Promise<{ updated: number; ids: string[] }> =>
    request("/studies/bulk-archive", { method: "POST", json: { ids } }),
  duplicate: (id: string): Promise<{ study: AdminStudy }> =>
    request(`/studies/${id}/duplicate`, { method: "POST" }),
  /** Permanently delete a study + all its interviews (voice, transcripts,
   *  insights, cost…). Server blocks this for `published` studies (409). */
  remove: (id: string): Promise<{ ok: true; deletedInterviews: number }> =>
    request(`/studies/${id}`, { method: "DELETE" }),
  cost: (
    id: string,
    range?: { startDate?: string; endDate?: string },
  ): Promise<StudyCost> => {
    const q = new URLSearchParams();
    if (range?.startDate) q.set("start_date", range.startDate);
    if (range?.endDate) q.set("end_date", range.endDate);
    const qs = q.toString();
    return request(`/studies/${id}/cost${qs ? `?${qs}` : ""}`);
  },
  transcripts: (
    id: string,
  ): Promise<{ filename: string; markdown: string }> =>
    request(`/studies/${id}/transcripts`),
};

export interface StudyCost {
  /** OpenAI spend (Realtime + insights tokens). */
  totalUsd: number;
  /** Reward unit cost (incl. provider commission); null = no reward set. */
  rewardUnitUsd: number | null;
  /** validInterviewCount × rewardUnitUsd. */
  rewardsUsd: number;
  /** totalUsd + rewardsUsd — the campaign's full cost. */
  grandTotalUsd: number;
  currency: "USD";
  interviewCount: number;
  validInterviewCount: number;
  /** Mean duration (seconds) across counted interviews; null when none. */
  avgDurationS: number | null;
  avgPerValid: number | null;
  source: "computed";
}

// ── Interviews ──────────────────────────────────────────────────────────────

export interface AdminInterviewListRow {
  id: string;
  userId: string;
  email: string;
  studyId: string;
  startedAt: string;
  endedAt: string | null;
  durationS: number | null;
  status: string;
  invalidReason: string | null;
  sentiment: string | null;
  userTurnCount: number | null;
  archived: boolean;
  hasAudio: boolean;
  /** When the gift-card email was sent; null = not rewarded yet. */
  rewardSentAt: string | null;
  /** The exact gift-card code emailed to this participant; null = none sent. */
  rewardCode: string | null;
}

export interface ListInterviewsParams {
  cursor?: string;
  limit?: number;
  studyId?: string;
  status?: string;
  sentiment?: string;
  archived?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

export const interviews = {
  list: (
    params: ListInterviewsParams = {},
  ): Promise<{
    interviews: AdminInterviewListRow[];
    nextCursor: string | null;
    total: number;
  }> => {
    const q = new URLSearchParams();
    if (params.cursor) q.set("cursor", params.cursor);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.studyId) q.set("study_id", params.studyId);
    if (params.status) q.set("status", params.status);
    if (params.sentiment) q.set("sentiment", params.sentiment);
    if (params.archived !== undefined)
      q.set("archived", String(params.archived));
    if (params.dateFrom) q.set("date_from", params.dateFrom);
    if (params.dateTo) q.set("date_to", params.dateTo);
    const qs = q.toString();
    return request(`/interviews${qs ? `?${qs}` : ""}`);
  },
  get: (id: string): Promise<AdminInterviewDetail> => request(`/interviews/${id}`),
  userDetails: (id: string): Promise<AdminUserDetails> =>
    request(`/interviews/${id}/user-details`),
  audioUrl: (id: string): Promise<{ url: string; expiresAt: number }> =>
    // Safari can't play webm — ask the api for an mp4-compatible URL (it
    // lazily transcodes webm originals to m4a and caches them). Chrome/
    // Firefox keep getting the original webm with zero extra latency.
    request(
      `/interviews/${id}/audio${canPlayWebm() ? "" : "?format=mp4"}`,
    ),
  archive: (id: string): Promise<{ interview: AdminInterviewDetail }> =>
    request(`/interviews/${id}/archive`, { method: "POST" }),
  unarchive: (id: string): Promise<{ interview: AdminInterviewDetail }> =>
    request(`/interviews/${id}/unarchive`, { method: "POST" }),
  bulkArchive: (
    ids: string[],
    archived: boolean,
  ): Promise<{ updated: number; ids: string[] }> =>
    request("/interviews/bulk-archive", {
      method: "POST",
      json: { ids, archived },
    }),
  /** CSV export (table columns + full transcript). Pass `ids` for selected
   *  rows, or `filters` to export EVERYTHING matching the filter chips
   *  server-side (not limited by the list's pagination). */
  exportCsv: (body: {
    ids?: string[];
    filters?: {
      studyIds?: string[];
      statuses?: string[];
      sentiments?: string[];
      dateFrom?: string;
      dateTo?: string;
    };
  }): Promise<{ filename: string; csv: string }> =>
    request("/interviews/export", { method: "POST", json: body }),
  insights: (
    id: string,
  ): Promise<{ insights: InterviewInsights; cached?: true }> =>
    request(`/interviews/${id}/insights`, { method: "POST" }),
};

export interface AdminInterviewDetail {
  id: string;
  userId: string;
  studyId: string;
  email: string;
  identitySnapshot: Record<string, unknown> | null;
  snapshotAt: string | null;
  startedAt: string;
  endedAt: string | null;
  durationS: number | null;
  status: string;
  invalidReason: string | null;
  transcript: Array<{ role: "user" | "assistant"; text: string; ts?: number }> | null;
  audioGcsPath: string | null;
  userTurnCount: number | null;
  usage: Record<string, unknown> | null;
  costUsd: number | null;
  insights: InterviewInsights | null;
  sentiment: string | null;
  archived: boolean;
  createdAt: string;
  /** When the gift-card email was sent; null = not rewarded yet. */
  rewardSentAt: string | null;
  /** The exact gift-card code emailed to this participant; null = none sent. */
  rewardCode: string | null;
}

export interface AdminUserDetails {
  email: string;
  snapshotAt: string | null;
  name: string | null;
  language: string | null;
  availablePlanIds: string[] | null;
  signupDate: string | null;
}

// ── Workspace prompts ──────────────────────────────────────────────────────

export const workspacePrompts = {
  list: (): Promise<{ prompts: WorkspacePrompt[] }> =>
    request("/workspace-prompts"),
  create: (input: {
    title: string;
    description?: string | null;
    content: string;
    isActive?: boolean;
  }): Promise<{ prompt: WorkspacePrompt }> =>
    request("/workspace-prompts", { method: "POST", json: input }),
  update: (
    id: string,
    patch: Partial<WorkspacePrompt>,
  ): Promise<{ prompt: WorkspacePrompt }> =>
    request(`/workspace-prompts/${id}`, { method: "PATCH", json: patch }),
  remove: (id: string): Promise<{ ok: true }> =>
    request(`/workspace-prompts/${id}`, { method: "DELETE" }),
  reorder: (
    items: Array<{ id: string; sortOrder: number }>,
  ): Promise<{ prompts: WorkspacePrompt[] }> =>
    request("/workspace-prompts/reorder", { method: "POST", json: { items } }),
};

// ── Rewards + plans (read-only) ────────────────────────────────────────────

export interface AdminReward {
  id: string;
  name: string;
  price: number | null;
  comment: string | null;
  createdAt: string;
}

export const rewards = {
  list: (): Promise<{ rewards: AdminReward[] }> => request("/rewards"),
  create: (input: {
    id: string;
    name: string;
    price?: number | null;
    comment?: string | null;
  }): Promise<{ reward: AdminReward }> =>
    request("/rewards", { method: "POST", json: input }),
  update: (
    id: string,
    patch: { name?: string; price?: number | null; comment?: string | null },
  ): Promise<{ reward: AdminReward }> =>
    request(`/rewards/${id}`, { method: "PATCH", json: patch }),
  remove: (id: string): Promise<void> =>
    request(`/rewards/${id}`, { method: "DELETE" }),
};

export interface AdminLeaplyPlan {
  id: string;
  name: string;
  description: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export const leaplyPlans = {
  list: (): Promise<{ plans: AdminLeaplyPlan[] }> => request("/leaply-plans"),
  update: (
    id: string,
    patch: { name?: string; description?: string; sortOrder?: number },
  ): Promise<{ plan: AdminLeaplyPlan }> =>
    request(`/leaply-plans/${encodeURIComponent(id)}`, {
      method: "PATCH",
      json: patch,
    }),
};

/** True when this browser can play webm/opus in an <audio> element.
 *  Chrome/Firefox: yes. Safari: no — we then request `?format=mp4` so the
 *  api serves a lazily-transcoded m4a instead. Memoised: the answer can't
 *  change within a session. */
let canPlayWebmCache: boolean | null = null;
function canPlayWebm(): boolean {
  if (canPlayWebmCache === null) {
    try {
      canPlayWebmCache =
        document.createElement("audio").canPlayType('audio/webm; codecs="opus"') !== "";
    } catch {
      canPlayWebmCache = true; // non-DOM environment — assume the common case
    }
  }
  return canPlayWebmCache;
}
